import {
  AspectRatio,
  Box,
  Center,
  Container,
  Heading,
  Icon,
  Stack,
  Text,
  Button,
} from '@chakra-ui/react';

import Row from '@inspire/ui/chakra/layout/Row';
import {
  inspireYellow,
  lightBeigeBg,
  lightYellowBg,
  sansFonts,
} from '@inspire/ui/static';
import {
  emphasisButtonProps,
  roundButtonProps,
} from '@inspire/ui/chakra/theme';
import { RightArrowIcon } from '@inspire/ui/icons';
import LinkButton from 'components/general/LinkButton';
import GenericHeroSection from 'components/landing-pages/HeroSection';
import PartnersSlider from 'components/general/PartnersSlider';
import Timeline from 'components/landing-pages/Timeline';
import SocialChannels from 'components/landing-pages/SocialChannels';
import VideoPlayer from 'components/general/VideoPlayer';
import { CONNEXION_PATH, LOGGED_IN_HOME_PATH } from 'lib/paths';
import {
  GTM_ACTION_LANDING_PAGE_LYCEEN,
  GTM_ACTION_LANDING_PAGE_PROF,
  GTM_ACTION_LANDING_PAGE_STUDENT,
  GTM_CATEGORY_LANDING_PAGES,
  pushEvent,
} from 'lib/gtm';
import { accessSSO } from 'lib/sso';
import { useMemoryStore } from 'lib/store';

const GenericLandingPage = ({ pageConfig }) => {
  const HeroSectionToUse = pageConfig.HeroSection ?? GenericHeroSection;

  // Get the redirect URL from the store for redirection after login
  const redirectTargetUrl = useMemoryStore((s) => s.redirectTargetUrl);
  const redirectUrlFromStore = redirectTargetUrl?.redirectUrl;

  return (
    <Box bg="white">
      {/* Header compensation */}
      <Row pt={['56px', '64px']}>
        <Container maxW="1000px" py={12}>
          <HeroSectionToUse {...pageConfig.hero} />
        </Container>
      </Row>

      <Row bgColor={lightYellowBg} py={12}>
        <Container maxW="container.lg">
          <Heading
            as="h2"
            fontFamily={sansFonts}
            textAlign="center"
            mt={0}
            mb={12}
          >
            Comment ça marche ?
          </Heading>
          <AspectRatio
            maxW="350px"
            width={['90%', null, '40%']}
            ratio={3 / 5}
            mx="auto"
          >
            <VideoPlayer
              url={pageConfig.videoCommentCaMarcheURL}
              light={pageConfig.videoCommentCaMarcheMiniatureUrl}
            />
          </AspectRatio>
          <Center mt={10}>
            <Button
              {...roundButtonProps}
              {...emphasisButtonProps}
              onClick={() => {
                pushEvent(
                  GTM_CATEGORY_LANDING_PAGES,
                  pageConfig.profil === 'prof'
                    ? GTM_ACTION_LANDING_PAGE_PROF
                    : pageConfig.profil === 'lyceen'
                    ? GTM_ACTION_LANDING_PAGE_LYCEEN
                    : GTM_ACTION_LANDING_PAGE_STUDENT,
                  "Je m'inscris"
                );
                accessSSO({
                  targetUrl: redirectUrlFromStore
                    ? `${CONNEXION_PATH}?redirectUrl=${redirectUrlFromStore}`
                    : `${CONNEXION_PATH}?redirectUrl=${LOGGED_IN_HOME_PATH}`,
                });
              }}
            >
              Je m'inscris <Icon ml={2} as={RightArrowIcon} />
            </Button>
          </Center>
        </Container>
      </Row>

      {pageConfig.video2Title && (
        <Row py={12}>
          <Container maxW="container.lg">
            <Stack
              direction={['column', null, 'row']}
              spacing={[10, null, 5]}
              justify="space-evenly"
            >
              {[
                {
                  title: pageConfig.video2Title,
                  text: pageConfig.video2Text,
                  url: pageConfig.video2URL,
                },
                {
                  title: pageConfig.video3Title,
                  text: pageConfig.video3Text,
                  url: pageConfig.video3URL,
                },
              ].map((x) => (
                <Box key={x.url}>
                  <Box textAlign="center" mb={8}>
                    <Heading
                      mt={0}
                      fontWeight="bold"
                      fontSize="2xl"
                      fontFamily={sansFonts}
                    >
                      {x.title}
                    </Heading>
                    <Text>{x.text}</Text>
                  </Box>
                  <Box
                    pos="relative"
                    border={`14px solid ${inspireYellow}`}
                    borderRadius={30}
                    overflow="hidden"
                    shadow="0 0 0 2px black, 7px 7px 0 0px black"
                    maxW="350px"
                    w={['100%', null, '300px', '350px']}
                    mx="auto"
                  >
                    <AspectRatio overflow="hidden" ratio={2.81 / 5}>
                      <VideoPlayer url={x.url} />
                    </AspectRatio>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Container>
        </Row>
      )}

      {pageConfig.subVideoContent ? (
        <Row py={12}>
          <Container maxW="container.lg">
            {pageConfig.subVideoContent}
          </Container>
        </Row>
      ) : null}

      <Row bgColor={lightBeigeBg} py={12}>
        <Container maxW="container.lg">
          <Timeline
            timelineContent={pageConfig.timelineContent}
            timelineSubCTA={pageConfig.timelineSubCTA}
            profil={pageConfig.profil}
          />
        </Container>
      </Row>

      <Row pt={8} pb={12}>
        <Container maxW="container.lg">
          <Stack
            direction={['column', null, 'row']}
            justify="space-between"
            spacing={{ md: 10 }}
            mt={6}
          >
            {pageConfig.roles.map((r, c) => (
              <Box
                key={c}
                width={{ base: '100%', md: '45%' }}
                textAlign={{ md: 'center' }}
              >
                <Heading as="h4" fontSize="lg" fontFamily={sansFonts}>
                  {r.role}
                </Heading>
                <Text minH="5rem" textAlign="left">
                  {r.text}
                </Text>
                <Center mt={5}>
                  <LinkButton
                    href={r.href}
                    colorScheme="black"
                    variant="outline"
                    {...roundButtonProps}
                  >
                    {r.button} <Icon ml={2} as={RightArrowIcon} />
                  </LinkButton>
                </Center>
              </Box>
            ))}
          </Stack>
        </Container>
      </Row>

      <Row bg={inspireYellow} py={10}>
        <Container maxW="container.lg">
          <SocialChannels />
        </Container>
      </Row>

      <Row pt={12} pb={6}>
        <Container maxW="container.lg">
          <Heading
            as="h3"
            mt={0}
            mb={0}
            fontFamily={sansFonts}
            textAlign="center"
          >
            Nos partenaires
          </Heading>
          <Text textAlign="center" fontFamily={sansFonts}>
            Merci à nos partenaires pour leur soutien, leur engagement et leur
            confiance dans INSPIRE.
          </Text>
          <PartnersSlider />
        </Container>
      </Row>
    </Box>
  );
};

export default GenericLandingPage;
